"use client";

import { useCallback, useRef } from "react";
import clsx from "clsx";
import { TextField, TextArea as AriaTextArea } from "react-aria-components";
import { SubmitCircleButton } from "@ui/components/core/SubmitCircleButton";
import { FileThumbnails } from "./FileThumbnails";
import { Dropzone } from "./Dropzone";
import type { PromptIslandProps } from "./types";

export function PromptIsland({
  className,
  text,
  onTextChange,
  textPlaceholder,
  textDisabled,
  textLoading,
  textRef,
  files,
  onFilesDrop,
  onRemoveFile,
  acceptedMimeTypes,
  maxFiles = 1,
  dropzoneLabel,
  dropzoneIcon,
  children,
  textAnnotation,
  toolbarLeft,
  toolbarRight,
  onSubmit,
  submitDisabled,
  submitLoading,
  submitLabel,
  error,
}: PromptIslandProps) {
  const dropzoneOpenRef = useRef<(() => void) | null>(null);
  const hasFiles = files && files.length > 0;
  const isTextReadOnly = !onTextChange;
  const showDropzone = !!acceptedMimeTypes && !hasFiles && !children;
  const showTextarea = (!!onTextChange || !!textPlaceholder) && !children;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey && onSubmit) {
        e.preventDefault();
        onSubmit();
      }
    },
    [onSubmit],
  );

  // In dropzone mode, the submit button opens the file picker
  const effectiveOnSubmit = showDropzone ? (onSubmit ?? (() => dropzoneOpenRef.current?.())) : onSubmit;

  return (
    <div
      className={clsx(
        "tw-flex tw-w-full tw-min-h-[160px] tw-flex-col tw-rounded-[30px] tw-bg-white tw-p-3 tw-outline-none tw-shadow-[0px_0px_1px_0px_rgba(0,0,0,0.4),0px_1px_2px_0px_rgba(0,0,0,0.04),0px_2px_2px_0px_rgba(0,0,0,0.04)]",
        className,
      )}
    >
      {children ? (
        children
      ) : (
        <>
          {hasFiles && <FileThumbnails files={files} onRemoveFile={onRemoveFile} isLoading={submitLoading} />}
          {showDropzone && (
            <Dropzone
              acceptedMimeTypes={acceptedMimeTypes}
              maxFiles={maxFiles}
              onFilesDrop={onFilesDrop}
              dropzoneLabel={dropzoneLabel}
              dropzoneIcon={dropzoneIcon}
              openRef={dropzoneOpenRef}
            />
          )}
          {showTextarea && (
            <div className={clsx("tw-flex tw-flex-1 tw-flex-col", isTextReadOnly ? "tw-px-3 tw-pt-2.5" : "tw-p-3")}>
              {isTextReadOnly ? (
                <p
                  className={clsx(
                    "tw-font-body tw-text-base tw-leading-normal tw-tracking-[0.01em] tw-line-clamp-3",
                    textLoading ? "shimmer-text" : "tw-text-black/40",
                  )}
                >
                  {text || textPlaceholder}
                </p>
              ) : (
                <TextField value={text} onChange={value => onTextChange!(value)} isDisabled={textDisabled}>
                  <AriaTextArea
                    ref={textRef}
                    onKeyDown={handleKeyDown}
                    placeholder={textPlaceholder}
                    rows={2}
                    className='tw-w-full tw-flex-1 tw-resize-none tw-bg-transparent tw-type-base tw-text-dark tw-placeholder-black/40 tw-outline-none tw-max-h-[calc(8lh+1px)] tw-overflow-y-auto [field-sizing:content]'
                  />
                </TextField>
              )}
            </div>
          )}
        </>
      )}

      {textAnnotation}

      {error && <p className='tw-type-xs tw-text-red-500 tw-px-3 tw-pt-2'>{error}</p>}

      {toolbarLeft !== null && (
        <div className='tw-mt-auto tw-flex tw-h-11 tw-items-center tw-justify-between'>
          <div>{toolbarLeft}</div>
          {toolbarRight ?? (
            <SubmitCircleButton
              onClick={effectiveOnSubmit}
              disabled={submitDisabled ?? !effectiveOnSubmit}
              loading={submitLoading}
              label={submitLabel}
            />
          )}
        </div>
      )}
    </div>
  );
}
